// Erreurs typees du plan de controle backend (api.rmf.tf). Permet aux composants UI de
// matcher sur le type (`if (err instanceof QuotaExceededError)`) plutot que sur des strings.

export class BackendError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BackendError';
  }
}

/** 503 + code='maintenance_mode' : le kill-switch SSM est actif (cf ADR 022). */
export class MaintenanceModeError extends BackendError {
  constructor(message?: string) {
    super(message ?? 'Service en maintenance');
    this.name = 'MaintenanceModeError';
  }
}

/** 503 + code='service_initializing' : le SSM `latest_ami_id` pointe encore sur le placeholder. */
export class ServiceInitializingError extends BackendError {
  constructor(message?: string) {
    super(message ?? "Service en cours d'initialisation");
    this.name = 'ServiceInitializingError';
  }
}

/** 503 + code='service_at_capacity' : quota AWS atteint, plus d'EC2 disponibles. */
export class ServiceAtCapacityError extends BackendError {
  constructor(message?: string) {
    super(message ?? 'Service sature, plus de capacite disponible');
    this.name = 'ServiceAtCapacityError';
  }
}

/** 403 + code='quota_exceeded' : l'utilisateur a depasse 4 h cumulees sur 24 h glissantes (cf ADR 020). */
export class QuotaExceededError extends BackendError {
  readonly usedSeconds: number;
  readonly limitSeconds: number;
  constructor(detail: { usedSeconds: number; limitSeconds: number; message?: string }) {
    super(detail.message ?? "Quota d'utilisation depasse");
    this.name = 'QuotaExceededError';
    this.usedSeconds = detail.usedSeconds;
    this.limitSeconds = detail.limitSeconds;
  }
}

/** 404 sur /stop : aucune session active pour ce sub. Cas non-fatal (deja arretee, sweep, etc.). */
export class SessionNotFoundError extends BackendError {
  constructor(message?: string) {
    super(message ?? 'Session introuvable');
    this.name = 'SessionNotFoundError';
  }
}

/**
 * Timeout cote front pendant le readiness. Deux origines :
 * - polling /status (~5 min, session.svelte.ts) : la Lambda /status a retourne state='booting'
 *   jusqu'au bout - EC2 KO, control_plane bloque, cold start anormalement long, ou cert TLS de
 *   session expire ou invalide (la sonde /status valide le cert, toute erreur reste 'booting') ;
 * - phase 3 /health/full (150 s, readiness-polling.ts), appele directement par le navigateur :
 *   rmf-web jamais fully_ready, ou echec TLS si le navigateur ne reconnait pas la chaine du
 *   cert (client sans ISRG Root X2 ni recuperation AIA, cf ADR 035).
 * Egalement levee par getValidTrustedToken() si appele avant phase=ready (hors timeout).
 * L'utilisateur peut retry, le sweep coupera l'EC2 a T+30 min.
 */
export class SessionNotReadyError extends BackendError {
  constructor(message?: string) {
    super(message ?? 'La session ne repond pas dans le delai imparti');
    this.name = 'SessionNotReadyError';
  }
}

/**
 * /status a retourne state='failed' : la session a echoue cote backend (EC2 terminated
 * unexpectedly, control_plane crash, healthcheck Lambda echoue plusieurs fois...). Le
 * field `reason` du backend est propage dans message pour debug.
 */
export class SessionFailedError extends BackendError {
  constructor(reason?: string) {
    super(reason ?? 'La session a echoue de maniere inattendue');
    this.name = 'SessionFailedError';
  }
}

/**
 * /status a retourne state='no_session' alors qu'on s'attendait a une session active : cas
 * rare ou le sweep DDB a coupe la session entre /start et le 1er /status. Different de
 * SessionNotFoundError (qui est non-fatale sur /stop). Ici c'est une situation anormale qui
 * doit etre signalee a l'utilisateur (proposer un retry / nouveau /start).
 */
export class SessionMissingError extends BackendError {
  constructor(message?: string) {
    super(message ?? 'Aucune session active trouvee cote backend');
    this.name = 'SessionMissingError';
  }
}

/** 401 sur n'importe quel call : le JWT a ete rejete par l'API Gateway Authorizer. */
export class UnauthorizedError extends BackendError {
  constructor(message?: string) {
    super(message ?? 'Token rejete par le backend');
    this.name = 'UnauthorizedError';
  }
}

/**
 * 409 sur POST /switch-world : un autre switch est deja en cours (lock asyncio actif cote
 * control_plane). Le caller doit attendre `switching=false` via /world-status avant de retry.
 * Cf ADR 030.
 */
export class SwitchAlreadyInProgressError extends BackendError {
  constructor(message?: string) {
    super(message ?? 'Un changement de world est deja en cours');
    this.name = 'SwitchAlreadyInProgressError';
  }
}

/** 400 sur POST /switch-world : world hors allowlist. Le selecteur UI devrait empecher ca. */
export class InvalidWorldError extends BackendError {
  constructor(message?: string) {
    super(message ?? "World non autorise");
    this.name = 'InvalidWorldError';
  }
}
